import { map, get, find, toLower, compact } from "lodash"
import { createSelector } from "reselect"
import moment from "moment"
import { PERMISSION_LEVELS, hasPermission } from "helpers/permissions"
import { upsellingTitle } from "helpers/op-pro"
import { Creators as ResourceCreators } from "modules/resources/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { getPermissions, getFavoritesAsObject, getUploadingItems } from "./items"
import { getBaskets } from "resources/baskets"

import Confirm from "components/confirm-dialog"
import EditSwap from "connected-components/edit-swap"
import saveRecentResources from "hocs/saveRecentResources"

const getSwaps = createSelector(
  [getBaskets],
  (swaps) =>
    map(swaps, swap => {
      const info = []

      if (swap.availableFrom) {
        info.push({ label: "Available from", value: moment(swap.availableFrom).format("ll") })
      }

      if (swap.terminationDate) {
        info.push({ label: "Terminated on", className: "termination-date", value: moment(swap.terminationDate).format("ll") })
      }

      return {
        ...swap,
        alias: swap.ticker,
        resourceType: "swap",
        label: swap.ticker,
        noData: !swap.availableFrom,
        info,
      }
    }),
)

export const getSwapsWithMeta = createSelector(
  [getSwaps, getPermissions, getFavoritesAsObject, getUploadingItems],
  (swaps, permissions, favorites, uploadingItems) => map(swaps, swap => {
    const { id, permissionLevel } = find(permissions, ({ type, alias }) => type === "SWAP" && alias === toLower(swap.ticker)) ?? {}
    return {
      ...swap,
      id,
      permissionLevel,
      shortType: "b",
      type: "Basket",
      isFavorite: get(favorites, swap.ticker, false),
      refresh: get(uploadingItems, id, false),
    }
  }),
)

export function getSwapMenuItems(account, swap, isOpPro) {
  const { toggleFavorite, editSwap, deleteSwap } = ResourceCreators
  const { open: openDialog, close: closeDialog } = DialogCreators
  const { add: addNotification } = NotificationCreators
  const { id, ticker, alias, isFavorite, permissionLevel } = swap

  const editable = hasPermission(permissionLevel, PERMISSION_LEVELS.WRITE)
  const deletable = hasPermission(permissionLevel, PERMISSION_LEVELS.OWNER)

  const EditSwapWithRecentResources = saveRecentResources(EditSwap, "selectedSwap.id")

  return compact([
    editable && {
      id: "edit",
      label: "Edit",
      action: (dispatch) => dispatch(openDialog({
        title: "Edit basket",
        // [TODO] [#184018795] JSX shouldn't be in selectors or models
        content: <EditSwapWithRecentResources
          selectedSwap={swap}
          saveSwap={(...args) => dispatch(editSwap(...args))}
        />,
      })),
    },
    {
      id: "toggleFavorite",
      label: isFavorite ? "Remove from favorites" : "Add to favorites",
      action: (dispatch) => {
        dispatch(toggleFavorite(ticker || id, "swap", !isFavorite))
        dispatch(addNotification("info", null, `${alias} successfully ${isFavorite ? "removed from" : "added to"} favorites`))
      },
    },
    editable && {
      id: "upload",
      label: "Upload positions",
      to: `/${account}/baskets/${alias}/upload`,
    },
    {
      id: "permissions",
      label: "View permissions",
      to: `/${account}/b/${alias}/permissions/members`,
      disabledText: isOpPro && upsellingTitle(isOpPro, "basket"),
    },
    deletable && {
      id: "delete",
      label: "Delete",
      className: "danger",
      action: (dispatch, navigate) => {
        dispatch(openDialog({
          title: `Delete basket: ${alias}`,
          content: <Confirm
            text={`Are you sure you want to delete ${alias}? All associated position data will be deleted.`}
            keyword={alias}
            cta="Delete basket"
            action={() => {
              dispatch(deleteSwap(ticker))
              navigate(`/${account}/home/resources`)
            }}
            onClose={() => dispatch(closeDialog())}
          />,
        }))
      },
    },
  ])
}
